import * as React from 'react';
import { createContext, useState, useEffect } from 'react';
import Progress from '../shared/Progress';
import { batch, useDispatch, useSelector } from 'react-redux';
import { calendarActiveSearch } from '../../../api/emailsApi/calendar';
import { changeAlertText } from '../../actions/index';
import { AttendeeSearchTable } from './AttendeeSearchTable';
import { SelectAttendee } from './SelectAttendee';
import { AttendeeSearchBar } from './AttendeeSearchBar';
import './AttendeeSearchContainer.scss';

export const SearchContext = createContext({});

const debounce = (cb, delay) => () => {
  const timeout = setTimeout(cb, delay);
  return () => clearTimeout(timeout);
};

/**
 * SearchContainer is a multi use component that holds most of the logic around presenting records
 * and performing searches.
 * @return {function}
 */
export const AttendeeSearchContainer = () => {
  const dispatch = useDispatch();
  const [searchResultsLoading, changeSearchResultsLoadingState] = useState(false);
  const outlookEventAttendees = useSelector((state) => state.calendar.eventAttendees);
  const currentAttendees = new Set(Object.values(outlookEventAttendees)
      .flatMap((value) => Array.isArray(value) ? value : [value]));

  // Track the current and prior search object to detect a change.
  const defaultSearchObject = { objectApiName: 'Contact' };
  const [searchObject, updateSearchObject] = useState(defaultSearchObject);
  const [records, updateSearchResults] = useState([]);
  const [searchQuery, updateSearchQuery] = useState('');

  const updateSearchObjectCallback = (apiName) => {
    updateSearchObject(apiName);
    updateSearchQuery('');
  };


  const validateResponse = (response, source) => {
    if (response instanceof Error) {
      const message = source === 'search' ? 'Error searching. Please retry.' : 'Error loading';
      updateSearchObjectCallback(defaultSearchObject);
      batch(() => {
        dispatch(changeAlertText(response.data?.Message || message, 'error'));
      });
      return false;
    }
    return true;
  };

  const updateRecordsTable = async () => {
    let newRecords = [];
    // If there is a query, perform an active search
    if (searchQuery) {
      changeSearchResultsLoadingState(true);
      const response = await calendarActiveSearch(searchObject.objectApiName, searchQuery, true);
      const validResponse = validateResponse(response, 'search');
      if (validResponse) {
        newRecords = response.data?.ContactRecords;
      }
    }

    updateSearchResults(newRecords);
    changeSearchResultsLoadingState(false);
  };

  useEffect(() => {
    updateRecordsTable();
  }, [searchObject]);

  const debounceEffect = () => {
    if (![1, 2].includes(searchQuery.length)) {
      updateRecordsTable();
    }
  };

  useEffect(debounce(() => {
    debounceEffect();
  }, 700), [searchQuery]);

  useEffect(() => {
    if (![0, 1, 2].includes(searchQuery.length)) {
      changeSearchResultsLoadingState(true);
    }
  }, [searchQuery]);

  const getElements = () => {
    const elements = [];
    if (records.length < 1 && !searchResultsLoading && searchQuery.length > 2) {
      elements.push(getNoResultsElement());
    }
    for (const attendee of records) {
      if (attendee.emailAddress) {
        elements.push(getAttendeeElement(attendee));
      }
    }
    return elements;
  };

  const getNoResultsElement = () => (
    <p className='no-results-tag'>No search results found, please refine your search</p>
  );

  const getAttendeeElement = (attendee) => (
    <SelectAttendee
      key={attendee.objectId}
      details={attendee}
      disabled={currentAttendees.has(attendee.emailAddress)}
    />
  );

  // If there is a search query, generate multiple tabs determined by header texts.
  return (
    <div id='search-container'>
      <SearchContext.Provider value={{
        searchObject,
        searchQuery,
        searchObjectUpdateCallback: updateSearchObjectCallback,
        searchQueryCallback: updateSearchQuery,
      }} >
        <AttendeeSearchBar />
        { searchResultsLoading && <Progress /> }
        <AttendeeSearchTable innerComponents={getElements()} />
      </SearchContext.Provider>
    </div>
  );
};
